import { Alert, Box, Button, CircularProgress, Collapse, Divider, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Navigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { BrowserAuthError, InteractionStatus } from "@azure/msal-browser";
import { ApiEnabledPage, LoginError, LoginParams, PasswordHideAdornment } from "../../../../WebCommon/src";
import { loginRequest } from "../auth";

export interface LoginPageProps extends ApiEnabledPage {
  readonly deviceLogin?: boolean
}

/**
 * Maps login error codes to user-friendly error messages.
 * Error codes must match between frontend (LoginError enum) and backend (EntraLoginErrorCode/LoginErrorCode).
 */
const getLoginErrorMessage = (error: LoginError): string => {
  switch (error) {
    case LoginError.BadLogin:
      return "Incorrect email or password.";
    case LoginError.DeviceInUse:
      return "Device already registered to another account.";
    case LoginError.UnauthorizedApp:
      return "Unauthorized application. Please contact support.";
    case LoginError.InvalidToken:
      return "Authentication failed. Please try signing in again.";
    case LoginError.NetworkError:
      return "Network error. Please check your connection and try again.";
    case LoginError.UnknownError:
    default:
      return "An unexpected error occurred. Please try again later.";
  }
};

export const LoginPage = (props: LoginPageProps) => {

  const { deviceLogin, baseApiUrl, authHelper } = props;
  const { instance, inProgress } = useMsal();
  const isMsalBusy = inProgress !== InteractionStatus.None;


  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [passwordValue, setPasswordValue] = useState<string>("");
  const [emailValue, setEmailValue] = useState<string>("");

  const [passwordError, setPasswordError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(authHelper.IsLoggedIn());

  const [isLoginDisabled, setIsLoginDisabled] = useState<boolean>(false);
  const [isLoginFailed, setIsLoginFailed] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<LoginError>(LoginError.Success);
  const [searchParams,] = useSearchParams();

  const onPasswordChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const password = event.target.value;
    setPasswordValue(password);
  };

  const onEmailChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const email = event.target.value;
    setEmailValue(email);
  };

  const enterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onSubmit();
    }
  };

  const onSubmit = async () => {
    if (emailValue === "") {
      setEmailError("Please enter an email");
    } else {
      setEmailError("");
    }

    if (passwordValue === "") {
      setPasswordError("Please enter a password");
    } else {
      setPasswordError("");
    }

    setIsLoginDisabled(true);

    const data: LoginParams = {
      Email: emailValue,
      Password: passwordValue,
    };

    const deviceId = searchParams.get("device");
    const secret = searchParams.get("secret");

    if (deviceId && secret) {
      data.DeviceId = deviceId;
      data.ClientSecret = secret;
    }

    const loginResponse = await fetch(baseApiUrl + "Login", {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (loginResponse.status === 200) {
      const json = await loginResponse.json();
      const redirectUri = searchParams.get("redirectUri");

      if (redirectUri) {
        const redirectRes = await fetch(`${redirectUri}?key=${json.apiKey}`);

        if (redirectRes && redirectRes.status === 200) {
          authHelper.Login(
            json.apiKey,
            json.email,
            json.isAdmin);
          setIsLoggedIn(true);
          setIsLoginFailed(false);
        }
      } else {
        authHelper.Login(
          json.apiKey,
          json.email,
          json.isAdmin);
        setIsLoggedIn(true);
        setIsLoginFailed(false);
      }

    } else if (loginResponse.status === 401) {
      const json = await loginResponse.json();
      setLoginError(json.error);
      setIsLoginFailed(true);
    } else {
      setIsLoginFailed(true);
    }
    setIsLoginDisabled(false);

  };

  const handleMicrosoftLogin = async () => {
    // Check if MSAL is already processing an interaction
    if (isMsalBusy) {
      console.warn("MSAL interaction already in progress, skipping");
      return;
    }

    setIsLoginDisabled(true);
    setIsLoginFailed(false);

    try {
      const response = await instance.loginPopup(loginRequest);

      const deviceId = searchParams.get("device");
      const secret = searchParams.get("secret");

      const entraResponse = await fetch(baseApiUrl + "LoginEntra", {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          IdToken: response.idToken,
          DeviceID: deviceId ?? undefined,
          ClientSecret: secret ?? undefined,
        }),
      });

      if (entraResponse.ok) {
        const json = await entraResponse.json();

        // Handle device login redirect (for FabricViewer)
        const redirectUri = searchParams.get("redirectUri");
        if (redirectUri) {
          const redirectRes = await fetch(`${redirectUri}?key=${json.apiKey}`);
          if (redirectRes && redirectRes.status === 200) {
            authHelper.Login(json.apiKey, json.email, json.isAdmin?.toString() ?? "false");
            setIsLoggedIn(true);
            console.log('[LoginPage] Device login complete, isLoggedIn set to true');
          }
        } else {
          authHelper.Login(json.apiKey, json.email, json.isAdmin?.toString() ?? "false");
          setIsLoggedIn(true);
        }
      } else if (entraResponse.status === 401) {
        const json = await entraResponse.json();
        setLoginError(json.Error ?? LoginError.UnknownError);
        setIsLoginFailed(true);
      } else {
        console.error("LoginEntra failed with status:", entraResponse.status);
        setLoginError(LoginError.UnknownError);
        setIsLoginFailed(true);
      }
    } catch (error) {
      // Handle MSAL-specific errors
      if (error instanceof BrowserAuthError) {
        if (error.errorCode === "interaction_in_progress") {
          // Clear MSAL cache to recover from stuck state
          clearMsalInteractionState();
          setLoginError(LoginError.UnknownError);
          setIsLoginFailed(true);
        } else if (error.errorCode === "user_cancelled") {
          // User closed the popup - not an error, just reset state
        } else {
          console.error("MSAL error:", error.errorCode, error.message);
          setLoginError(LoginError.NetworkError);
          setIsLoginFailed(true);
        }
      } else {
        console.error("Microsoft login failed:", error);
        setLoginError(LoginError.NetworkError);
        setIsLoginFailed(true);
      }
    }

    setIsLoginDisabled(false);
  };

  /**
   * Clears MSAL interaction state from localStorage when stuck in "interaction_in_progress".
   * This allows retry after a failed or cancelled popup.
   */
  const clearMsalInteractionState = () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Clear interaction-related keys but preserve tokens/accounts for silent auth
      if (key && (key.includes('interaction') || key.includes('request'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log('Clearing MSAL interaction key:', key);
      localStorage.removeItem(key);
    });
  };

  if (isLoggedIn && !deviceLogin) {
    return <Navigate replace to="/" />;
  } else {

    return (
      <Box className="loginSignupBox">
        <Typography variant="h4">Login</Typography>
        <br />
        <Stack>
          <TextField
            required
            autoComplete="username"
            helperText={emailError}
            error={emailError === "" ? false : true}
            onKeyDown={enterKeyDown}
            onChange={onEmailChanged}
            sx={{ mb: "1vh" }}
            label="Email" />
          <TextField
            required
            helperText={passwordError}
            error={passwordError === "" ? false : true}
            onKeyDown={enterKeyDown}
            onChange={onPasswordChanged}
            label="Password"
            InputProps={{
              endAdornment: <PasswordHideAdornment hidePassword={hidePassword} setHidePassword={setHidePassword} />,
            }}
            type={hidePassword ? "password" : "text"}
            autoComplete="current-password"
            sx={{ "mb": "2vh" }}
          />
          <Button
            disabled={isLoginDisabled}
            onClick={onSubmit}
            variant="contained"
            startIcon={isLoginDisabled ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isLoginDisabled ? "Logging in..." : "Login"}
          </Button>

          <Divider sx={{ my: 2 }}>or</Divider>

          <Button
            disabled={isLoginDisabled || isMsalBusy}
            onClick={handleMicrosoftLogin}
            variant="outlined"
            fullWidth
            startIcon={(isLoginDisabled || isMsalBusy) ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {(isLoginDisabled || isMsalBusy) ? "Signing in..." : "Sign in with Microsoft"}
          </Button>

          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            New user? Sign in with Microsoft to create an account automatically.
          </Typography>
          <Collapse in={isLoginFailed}>
            <Alert variant="outlined" severity="error">
              {getLoginErrorMessage(loginError)}
            </Alert>
          </Collapse>
        </Stack>
      </Box>);
  }
};

export default LoginPage;
